import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { format } from 'date-fns';
import { MedicalRecord, AnimalGroup } from '../../../../types/animal';

interface BulkRecordDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (record: MedicalRecord) => void;
  loading: boolean;
  group: AnimalGroup;
  medicalRecordTypes: string[];
  initialRecord?: MedicalRecord | null;
}

const BulkRecordDialog: React.FC<BulkRecordDialogProps> = ({ 
  open, 
  onClose, 
  onSave, 
  loading, 
  group,
  medicalRecordTypes,
  initialRecord
}) => {
  const [record, setRecord] = useState<MedicalRecord | null>(null);

  useEffect(() => {
    if (initialRecord) {
      setRecord(initialRecord);
      setSelectedAnimals(initialRecord.affectedAnimals || []);
    } else {
      const defaultRecord: MedicalRecord = {
        id: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        type: 'routine',
        title: '',
        description: '',
        performedBy: '',
        status: 'completed',
        medications: [],
        cost: 0,
        result: '',
        isBulkTreatment: true,
        affectedAnimals: group?.individuals?.map(animal => animal.id) || [],
        treatmentGroup: group?.id || '',
        treatmentSchedule: {
          frequency: 'daily',
          startDate: format(new Date(), 'yyyy-MM-dd'),
          endDate: '',
          customDays: [],
          repeatCount: 1,
          notificationEnabled: false
        }
      };
      setRecord(defaultRecord);
      setSelectedAnimals(defaultRecord.affectedAnimals || []);
    }
  }, [initialRecord, group]);

  const [selectedAnimals, setSelectedAnimals] = useState<string[]>(group?.individuals?.map(animal => animal.id) || []);
  const allAnimalIds = group?.individuals?.map(a => a.id) || [];

  const handleAnimalSelection = (animalId: string) => {
    if (selectedAnimals.includes(animalId)) {
      const newSelected = selectedAnimals.filter(id => id !== animalId);
      setSelectedAnimals(newSelected);
      setRecord(prev => prev ? {
        ...prev,
        affectedAnimals: newSelected
      } : null);
    } else {
      const newSelected = [...selectedAnimals, animalId];
      setSelectedAnimals(newSelected);
      setRecord(prev => prev ? {
        ...prev,
        affectedAnimals: newSelected
      } : null);
    }
  };

  const handleToggleAll = (checked: boolean) => {
    const newSelected = checked ? allAnimalIds : [];
    setSelectedAnimals(newSelected);
    setRecord(prev => prev ? {
      ...prev,
      affectedAnimals: newSelected
    } : null);
  };

  if (!record) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Bulk Medical Record</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Date"
              value={record.date}
              onChange={e => setRecord({ ...record, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Record Type"
              value={record.type}
              onChange={e => setRecord({ ...record, type: e.target.value as any })}
              required
            >
              {medicalRecordTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              value={record.title}
              onChange={e => setRecord({ ...record, title: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={record.description}
              onChange={e => setRecord({ ...record, description: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Performed By"
              value={record.performedBy}
              onChange={e => setRecord({ ...record, performedBy: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Cost (Ksh)"
              value={record.cost || ''}
              onChange={e => setRecord({ ...record, cost: Number(e.target.value) })}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Treatment Schedule
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Frequency"
                  value={record.treatmentSchedule?.frequency}
                  onChange={e => setRecord({
                    ...record,
                    treatmentSchedule: {
                      ...record.treatmentSchedule!,
                      frequency: e.target.value as 'daily' | 'weekly' | 'monthly' | 'custom'
                    }
                  })}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Repeat Count"
                  value={record.treatmentSchedule?.repeatCount}
                  onChange={e => setRecord({
                    ...record,
                    treatmentSchedule: {
                      ...record.treatmentSchedule!,
                      repeatCount: parseInt(e.target.value)
                    }
                  })}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Affected Animals ({selectedAnimals.length} selected)
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      indeterminate={selectedAnimals.length > 0 && selectedAnimals.length < allAnimalIds.length}
                      checked={selectedAnimals.length === allAnimalIds.length && allAnimalIds.length > 0}
                      onChange={(e) => handleToggleAll(e.target.checked)}
                    />
                  }
                  label={`Select all (${allAnimalIds.length})`}
                />
                {group?.individuals?.map((animal) => (
                  <FormControlLabel
                    key={animal.id}
                    control={
                      <Checkbox
                        checked={selectedAnimals.includes(animal.id)}
                        onChange={() => handleAnimalSelection(animal.id)}
                      />
                    }
                    label={`${animal.name} (${animal.tagId || 'No Tag'})`}
                  />
                ))}
              </FormGroup>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={() => onSave(record)} 
          variant="contained" 
          color="primary"
          disabled={loading || !record.title || !record.description || !record.performedBy}
        >
          Save Record
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkRecordDialog;
